// components/UnAuthorized.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearUser } from '../store/userSlice';

const UnAuthorized = () => {
  const [countdown, setCountdown] = useState(15);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Clear auth data after component mounts
    const timeoutId = setTimeout(() => {
      localStorage.removeItem('token');
      dispatch(clearUser());
    }, 0);

    // Start countdown
    const intervalId = setInterval(() => {
      setCountdown((prevCount) => {
        if (prevCount <= 1) {
          // Navigate after clearing interval to avoid state updates during render
          setTimeout(() => {
            navigate('/login');
          }, 0);
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once

  // Calculate progress for circular timer
  const progress = ((15 - countdown) / 15) * 100;
  const strokeDashoffset = 283 - (283 * progress) / 100;

  return (
    <div className="min-h-screen relative overflow-hidden bg-purple-700 flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-red-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-red-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main content card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 md:p-10 shadow-2xl border border-gray-700/50">
          {/* Lock icon with pulse animation */}
          <div className="relative mx-auto w-24 h-24 mb-8">
            <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping"></div>
            <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping animation-delay-200"></div>
            <div className="relative flex items-center justify-center w-24 h-24 bg-red-500/10 rounded-full border-2 border-red-500/50">
              <svg 
                className="w-12 h-12 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                />
              </svg>
            </div>
          </div>

          {/* Title and message */}
          <h1 className="text-3xl md:text-4xl font-bold text-white text-center mb-2">
            Unauthorized
          </h1>
          <p className="text-red-400 text-center font-semibold text-lg mb-4 tracking-wider">
            ERROR 401
          </p>
          <p className="text-gray-400 text-center mb-8 leading-relaxed">
            Your session has expired or you don't have permission to access this resource. 
            Please log in again to continue.
          </p>

          {/* Countdown timer */}
          <div className="mb-8">
            <p className="text-gray-500 text-center text-sm uppercase tracking-wide mb-4">
              Redirecting to login in
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="36"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-gray-700"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="36"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray="283"
                    strokeDashoffset={strokeDashoffset}
                    className="text-red-500 transition-all duration-1000 ease-linear"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-red-500">{countdown}</span>
                </div>
              </div>
              <span className="text-gray-400 text-sm font-medium">seconds</span>
            </div>
          </div>

          {/* Go to Login button */}
          <Link
            to='/login'
            className="w-full group relative flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-red-500/25 focus:outline-none focus:ring-4 focus:ring-red-500/50"
          >
            <span className="relative z-10">Go to Login</span>
            <svg 
              className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 5l7 7-7 7" 
              />
            </svg>
            <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
          </Link>

          {/* Bottom decoration */}
          <div className="mt-8 flex justify-center">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce animation-delay-200"></div>
              <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce animation-delay-400"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnAuthorized;
